// La salle d'une séance (D-123, maquette s3) : choisie dans la liste des salles du club, plus en texte libre ; ce qui
// chevauche et ce qui est libre (dans cette salle, dans une autre), dit avant d'enregistrer, jamais bloquant. Le bureau
// ajoute une salle sans quitter la fiche. Le lieu affiché suit le nom de la salle (la base le tient). Module éteint
// (D-122) ou match à l'extérieur : le « Lieu » en texte libre, comme avant.
import { clubHalls, hallsBusy, type HallLite } from '../lib/salles-api';
import { addDays, checkProposal, clashText, freeText, hm, ymd, type Busy, type Check } from '../domain/salles';
import type { Ctx } from './context';
import { $, $$, html, mount } from './dom';
import { openHallForm } from './salles';
import { sallesOn } from './salles-module';

export interface HallWhen { day: string; start: string; end: string; weekdays: number[] | null; until: string | null }
export interface HallFieldOpts {
  ctx: Ctx;
  clubId: () => string | null;
  teamId: () => string;
  eventId?: string | null;
  current?: { hall_id?: string | null; location: string | null } | null;
  when: () => HallWhen;
  /** « Lieu » en texte libre (un match à l'extérieur). */
  free: () => boolean;
  /** « Prendre 14h00 » : l'heure (et le jour) d'un créneau libre. */
  setTime: (day: string, start: string, end: string) => void;
}
export interface HallField { value(): { hall_id: string | null; location: string | null }; refresh(): void }

/** La place du champ dans le formulaire (rempli par wireHallField). */
export const hallFieldHtml = (location: string | null = null) => html`
  <div class="sal-pick form" data-hall-field>
    <label class="field" data-hall-sel><span>Salle</span><select name="hall_id" data-guide="sal-pick"><option value="">…</option></select></label>
    <div class="sal-note" data-hall-check hidden data-guide="sal-check"></div>
    <label class="field" data-hall-free hidden><span>Lieu</span><input name="location" maxlength="120" value="${location ?? ''}" placeholder="ex. Salle des sports de l’adversaire"></label>
  </div>`;

export function wireHallField(box: HTMLElement, o: HallFieldOpts): HallField {
  const sel = $<HTMLSelectElement>(box, 'select[name=hall_id]')!;
  const freeIn = $<HTMLInputElement>(box, 'input[name=location]')!;
  const note = $(box, '[data-hall-check]')!;
  const on = sallesOn(o.ctx);
  let halls: HallLite[] = [];
  let busy: Busy | null = null;
  let club: string | null = null;
  let check: Check | null = null;
  let timer = 0;
  const bureau = () => !!club && o.ctx.adminOf.some(c => c.id === club);
  const prevText = o.current && !o.current.hall_id ? o.current.location : null;

  const fill = (pick: string | null) => {
    const sport = halls.filter(h => h.kind !== 'reception'), rec = halls.filter(h => h.kind === 'reception');
    sel.innerHTML = html`${sport.map(h => html`<option value="${h.id}">${h.name}</option>`)}
      ${rec.length ? html`<optgroup label="Salles de réception">${rec.map(h => html`<option value="${h.id}">${h.name}</option>`)}</optgroup>` : ''}
      <option value="">À préciser${prevText ? ` (${prevText})` : ''}</option>
      ${bureau() ? html`<option value="__add">＋ Ajouter une salle…</option>` : ''}`.s;
    sel.value = pick && halls.some(h => h.id === pick) ? pick : '';
  };
  const load = async (pick?: string | null) => {
    const c = o.clubId();
    if (!c) return;
    if (c !== club || pick !== undefined) {
      club = c;
      halls = await clubHalls(c).catch(() => []);
      const want = pick ?? o.current?.hall_id ?? (o.current ? null : halls.find(h => h.kind === 'gymnase')?.id ?? null);
      fill(want ?? null);
      const t = ymd(new Date());
      busy = await hallsBusy(c, t, addDays(t, 55)).catch(() => null);
    }
    paint();
  };

  const paint = () => {
    const free = !on || o.free();
    $(box, '[data-hall-sel]')!.hidden = free;
    $(box, '[data-hall-free]')!.hidden = !free;
    check = null;
    if (!free && busy && sel.value && sel.value !== '__add') {
      const w = o.when();
      if (w.day && w.start && w.end) check = checkProposal(busy, { hallId: sel.value, teamId: o.teamId(), eventId: o.eventId ?? null, ...w });
    }
    note.hidden = !check?.clash;
    if (!check?.clash) { mount(note, html``); return; }
    const c = check;
    mount(note, html`<p><strong>${clashText(c.clash!)}</strong></p><p class="muted small">${freeText(c)}</p>
      ${c.here.length || c.elsewhere.length ? html`<div class="btn-row">
        ${c.here.map((x, i) => html`<button type="button" class="btn btn-ghost btn-sm" data-here="${i}">Prendre ${hm(x.start)}</button>`)}
        ${c.elsewhere.map((x, i) => html`<button type="button" class="btn btn-ghost btn-sm" data-else="${i}">${halls.find(h => h.id === x.hallId)?.name ?? 'Cette salle'}</button>`)}</div>` : ''}`);
    $$<HTMLButtonElement>(note, '[data-here]').forEach(b => b.addEventListener('click', () => {
      const x = c.here[Number(b.dataset.here)]!; o.setTime(x.day, x.start, x.end); paint();
    }));
    $$<HTMLButtonElement>(note, '[data-else]').forEach(b => b.addEventListener('click', () => {
      sel.value = c.elsewhere[Number(b.dataset.else)]!.hallId; paint();
    }));
  };

  sel.addEventListener('change', () => {
    if (sel.value !== '__add') { paint(); return; }
    const c = club;
    sel.value = '';
    if (!c) return;
    openHallForm(c, null, halls.map(h => ({ ...h })), async (_gone, id) => { await load(id ?? null); });
  });
  if (on) void load(); else paint();

  return {
    value() {
      if (!on || o.free()) return { hall_id: null, location: freeIn.value.trim() || null };
      const h = halls.find(x => x.id === sel.value);
      return h ? { hall_id: h.id, location: h.name } : { hall_id: null, location: prevText };
    },
    refresh() { window.clearTimeout(timer); timer = window.setTimeout(() => { if (o.clubId() !== club && on) void load(); else paint(); }, 120); },
  };
}
